import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { EmptyState } from "../molecules/EmptyState";
import { Skeleton } from "../molecules/Skeleton";
import { EventCard, type EventCardProps } from "./EventCard";

export interface EventTimelineItem extends EventCardProps {
	id: string;
}

export interface EventTimelineProps {
	events: EventTimelineItem[];
	loading?: boolean;
	emptyMessage?: string;
	className?: string;
}

function EventTimeline({
	events,
	loading = false,
	emptyMessage = "No events found.",
	className,
}: EventTimelineProps): ReactNode {
	if (loading) {
		return (
			<div
				className={cn("flex flex-col gap-3", className)}
				aria-label="Loading events"
			>
				{Array.from({ length: 3 }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders have no stable keys
					<Skeleton key={i} className="h-32 w-full" />
				))}
			</div>
		);
	}

	if (events.length === 0) {
		return <EmptyState title={emptyMessage} className={className} />;
	}

	return (
		<ol
			className={cn("flex flex-col gap-3", className)}
			aria-label="Event timeline"
		>
			{events.map((event) => {
				const { id, ...cardProps } = event;
				return (
					<li key={id}>
						<EventCard {...cardProps} />
					</li>
				);
			})}
		</ol>
	);
}
EventTimeline.displayName = "EventTimeline";

export default EventTimeline;
export { EventTimeline };
